import { useState, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import {
  legacyImportService,
  LegacyRow,
  PreviewResponse,
  ImportResponse,
} from '../../services/legacy-import.service';
import { FileDropzone } from '../../components/etl/FileDropzone';

type Stage = 'idle' | 'preview' | 'importing' | 'done';

const TARGET_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  expenses: { label: 'Egresos de caja', icon: '💸', color: '#dc2626' },
  internal_movements: { label: 'Movimientos internos', icon: '🔄', color: '#7c3aed' },
  payroll_cycles: { label: 'Ciclos de nómina', icon: '💼', color: '#0891b2' },
};

const ANOMALY_LABELS: Record<string, string> = {
  invalid_date: 'Fecha inválida',
  week_year_mismatch: 'Año de semana inconsistente',
  unmapped_folio: 'Folio no reconocido',
  missing_concept: 'Concepto faltante',
  duplicate_external_id: 'ID duplicado',
};

export function LegacyImportPage() {
  const [stage, setStage] = useState<Stage>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<LegacyRow[]>([]);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'expenses' | 'internal_movements' | 'payroll_cycles'>('expenses');
  const [resendMessage, setResendMessage] = useState<string>("");

  // Parsear el Excel al subirlo
  const handleFileSelect = useCallback((selectedFile: File) => {
    setFile(selectedFile);
    setError(null);
    setPreview(null);
    setImportResult(null);
    setStage('idle');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const result = e.target?.result;
        if (!result) {
          setError('Error al leer el archivo');
          return;
        }

        const workbook = XLSX.read(result, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { defval: '' }) as LegacyRow[];

        const cleaned = jsonData.filter((r) =>
          Object.values(r).some((v) => v !== undefined && v !== null && v !== '')
        );

        if (cleaned.length === 0) {
          setError('El archivo no contiene filas con datos');
          return;
        }

        setRows(cleaned);
      } catch (err: any) {
        setError(err.message || 'Error al procesar el archivo');
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  }, []);

  // Ejecutar preview
  const handlePreview = useCallback(async () => {
    if (rows.length === 0) return;
    setIsProcessing(true);
    setError(null);

    try {
      const result = await legacyImportService.preview(rows);
      setPreview(result);
      setStage('preview');
    } catch (err: any) {
      setError(err.message || 'Error al generar preview');
    } finally {
      setIsProcessing(false);
    }
  }, [rows]);

  // Ejecutar importación
  const handleImport = useCallback(async () => {
    if (rows.length === 0 || !preview) return;
    setIsProcessing(true);
    setStage("importing");
    setError(null);

    try {
        // 1. Extraer todos los external_ids de las filas
        const allExternalIds = rows
        .map((r: any) => String(r.ID || "").trim())
        .filter((id: string) => id.length > 0);

        // 2. Preguntar al backend cuáles ya existen
        setResendMessage("Verificando registros ya importados...");
        const existing = await legacyImportService.checkExisting(allExternalIds);

        // 3. Filtrar las filas que ya existen
        const newRows = rows.filter((r: any) => {
        const id = String(r.ID || "").trim();
        return !id || !existing.has(id);
        });

        setResendMessage(
        `Registros nuevos: ${newRows.length}. Ya importados: ${existing.size}.`
        );

        // 4. Si no hay nuevos, terminar
        if (newRows.length === 0) {
        setImportResult({
            success: true,
            imported: 0,
            failed: 0,
            errors: [],
            summary: {
            expenses: 0,
            internalMovements: 0,
            payrollCycles: 0,
            skipped: existing.size,
            },
            normalizationErrors: [],
            timestamp: new Date().toISOString(),
        } as any);
        setStage("done");
        setIsProcessing(false);
        return;
        }

        // 5. Importar solo los nuevos
        const result = await legacyImportService.import(newRows as any);
        setImportResult(result);
        setStage("done");
    } catch (err: any) {
        setError(err.message || "Error al importar");
        setStage("preview");
    } finally {
        setIsProcessing(false);
    }
    }, [rows, preview]);

  // Reset
  const handleReset = useCallback(() => {
    setFile(null);
    setRows([]);
    setPreview(null);
    setImportResult(null);
    setStage('idle');
    setError(null);
  }, []);

  // Stats derivados
  const totalAnomalies = useMemo(() => {
    if (!preview) return 0;
    return Object.values(preview.anomalies).reduce((sum, n) => sum + n, 0);
  }, [preview]);

  const previewRows = useMemo(() => {
    if (!preview) return [];
    return preview.preview[activeTab] || [];
  }, [preview, activeTab]);

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Migración de Egresos Legacy</h1>
        <p className="text-gray-600">
          Importa el libro de egresos en Excel. El sistema clasificará automáticamente
          cada registro según su flujo: egresos de caja, movimientos internos o ciclos de nómina.
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="info-panel error">
          <span className="info-panel-icon">❌</span>
          <div className="info-panel-body">
            <p className="info-panel-text">{error}</p>
          </div>
        </div>
      )}

      {/* PASO 1: Carga */}
      {stage === 'idle' && (
        <div className="step-card">
          <div className="step-header">
            <span className="step-number">1</span>
            <div>
              <h3 className="step-title">Carga el archivo Excel de egresos</h3>
              <p className="step-subtitle">
                Formato esperado: FOLIO, FECHA del Documento, MES, CONCEPTO, COMENTARIOS,
                TIPO, MOVIMIENTO, year, ID, INPUT DATE, INPUT TIME, monto
              </p>
            </div>
          </div>

          {!file ? (
            <FileDropzone
              onFileSelect={handleFileSelect}
              accept=".xlsx,.xls"
              maxSize={50}
              label="Arrastra tu libro de egresos aquí"
            />
          ) : (
            <div className="file-preview">
              <div className="file-preview-info">
                <span className="file-preview-icon">📄</span>
                <div style={{ minWidth: 0 }}>
                  <p className="file-preview-name">{file.name}</p>
                  <p className="file-preview-meta">
                    {(file.size / 1024).toFixed(2)} KB • {rows.length} filas detectadas
                  </p>
                </div>
              </div>
              <button onClick={handleReset} className="btn btn-ghost btn-sm">
                🗑️ Cambiar archivo
              </button>
            </div>
          )}

          {rows.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <button
                onClick={handlePreview}
                disabled={isProcessing}
                className="btn btn-primary btn-block"
                style={{ padding: '14px' }}
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Analizando {rows.length} filas...
                  </span>
                ) : (
                  `🔍 Analizar ${rows.length} filas`
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* PASO 2: Preview */}
      {stage === 'preview' && preview && (
        <>
          {/* Resumen de distribución */}
          <div className="step-card active">
            <div className="step-header">
              <span className="step-number">2</span>
              <div style={{ flex: 1 }}>
                <h3 className="step-title">Distribución detectada</h3>
                <p className="step-subtitle">
                  {preview.valid} de {preview.total} filas válidas
                </p>
              </div>
            </div>

            <div className="cards-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              <DistributionCard
                target="expenses"
                count={preview.distribution.expenses}
                total={preview.valid}
              />
              <DistributionCard
                target="internal_movements"
                count={preview.distribution.internal_movements}
                total={preview.valid}
              />
              <DistributionCard
                target="payroll_cycles"
                count={preview.distribution.payroll_cycles}
                total={preview.valid}
              />
            </div>

            {/* Anomalías */}
            {totalAnomalies > 0 && (
              <div className="info-panel warning" style={{ marginTop: '16px' }}>
                <span className="info-panel-icon">⚠️</span>
                <div className="info-panel-body">
                  <p className="info-panel-title">
                    {totalAnomalies} anomalías detectadas
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px' }}>
                    {Object.entries(preview.anomalies).map(([flag, count]) => (
                      <span
                        key={flag}
                        className="badge badge-warning"
                        title={flag}
                      >
                        {ANOMALY_LABELS[flag] || flag}: {count}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Errores de normalización */}
            {preview.normalizeErrors.length > 0 && (
              <div className="info-panel error" style={{ marginTop: '16px' }}>
                <span className="info-panel-icon">❌</span>
                <div className="info-panel-body" style={{ width: '100%' }}>
                  <p className="info-panel-title">
                    {preview.normalizeErrors.length} filas no se pudieron procesar
                  </p>
                  <div className="error-list">
                    {preview.normalizeErrors.slice(0, 20).map((err, i) => (
                      <div key={i} className="error-list-item">
                        <strong>Fila {err.row}:</strong> {err.message}
                      </div>
                    ))}
                    {preview.normalizeErrors.length > 20 && (
                      <div className="error-list-item" style={{ fontStyle: 'italic' }}>
                        ... y {preview.normalizeErrors.length - 20} más
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Preview por target */}
          <div className="step-card active">
            <div className="step-header">
              <span className="step-number">3</span>
              <div style={{ flex: 1 }}>
                <h3 className="step-title">Vista previa por destino</h3>
                <p className="step-subtitle">Primeros 5 registros de cada categoría</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="tabs-bar">
              {(['expenses', 'internal_movements', 'payroll_cycles'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveTab(t)}
                  className={`tab-button ${activeTab === t ? 'active' : ''}`}
                >
                  {TARGET_LABELS[t].icon} {TARGET_LABELS[t].label}
                  <span style={{
                    marginLeft: '8px',
                    background: 'var(--bg)',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '11px',
                  }}>
                    {preview.distribution[t]}
                  </span>
                </button>
              ))}
            </div>

            {/* Tabla */}
            {previewRows.length === 0 ? (
              <div className="empty-state" style={{ padding: '32px' }}>
                <p className="empty-state-text">
                  No hay registros en esta categoría
                </p>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Folio origen</th>
                      <th>Concepto</th>
                      <th>Rubro</th>
                      <th>Departamento</th>
                      <th style={{ textAlign: 'right' }}>Monto</th>
                      <th>Anomalías</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: '12px' }}>{row.entry_date || '—'}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                          {row.folio_origin}
                        </td>
                        <td style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.concept}
                        </td>
                        <td style={{ fontSize: '12px' }}>{row.category_name}</td>
                        <td style={{ fontSize: '12px', color: 'var(--gray)' }}>
                          {row.department_name || '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                          {new Intl.NumberFormat('es-MX', {
                            style: 'currency',
                            currency: 'MXN',
                          }).format(row.amount)}
                        </td>
                        <td>
                          {row.anomaly_flags.length === 0 ? (
                            <span className="badge badge-success" style={{ fontSize: '10px' }}>
                              OK
                            </span>
                          ) : (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {row.anomaly_flags.map((f) => (
                                <span
                                  key={f}
                                  className="badge badge-warning"
                                  style={{ fontSize: '10px' }}
                                  title={ANOMALY_LABELS[f] || f}
                                >
                                  {ANOMALY_LABELS[f] || f}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Botón de importar */}
            <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
              <button
                onClick={handleReset}
                disabled={isProcessing}
                className="btn btn-ghost"
                style={{ flex: 1 }}
              >
                Cancelar
              </button>
              <button
                onClick={handleImport}
                disabled={isProcessing}
                className="btn btn-primary"
                style={{ flex: 2 }}
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Importando...
                  </span>
                ) : (
                  `💾 Importar ${preview.valid} registros`
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* PASO 3: Resultado */}
      {stage === 'done' && importResult && (
        <div className="step-card active">
          <div className="step-header">
            <span className="step-number">✓</span>
            <div style={{ flex: 1 }}>
              <h3 className="step-title">Importación completada</h3>
              <p className="step-subtitle">
                {importResult.imported} registros importados correctamente
              </p>
            </div>
          </div>

          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <StatBlock
              icon="💸"
              label="Egresos de caja"
              value={importResult.summary.expenses}
              color="#dc2626"
            />
            <StatBlock
              icon="🔄"
              label="Movimientos internos"
              value={importResult.summary.internalMovements}
              color="#7c3aed"
            />
            <StatBlock
              icon="💼"
              label="Ciclos de nómina"
              value={importResult.summary.payrollCycles}
              color="#0891b2"
            />
            <StatBlock
              icon="⏭️"
              label="Omitidos (duplicados)"
              value={importResult.summary.skipped}
              color="#64748b"
            />
          </div>

          {importResult.failed > 0 && (
            <div className="info-panel error" style={{ marginTop: '16px' }}>
              <span className="info-panel-icon">❌</span>
              <div className="info-panel-body" style={{ width: '100%' }}>
                <p className="info-panel-title">
                  {importResult.failed} filas fallaron
                </p>
                <div className="error-list">
                  {importResult.errors.slice(0, 20).map((err, i) => (
                    <div key={i} className="error-list-item">
                      <strong>Fila {err.row}:</strong> {err.message}
                    </div>
                  ))}
                  {importResult.errors.length > 20 && (
                    <div className="error-list-item" style={{ fontStyle: 'italic' }}>
                      ... y {importResult.errors.length - 20} más
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
            <button onClick={handleReset} className="btn btn-primary btn-block">
              Importar otro archivo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// SUBCOMPONENTES
// ============================================================

function DistributionCard({
  target,
  count,
  total,
}: {
  target: string;
  count: number;
  total: number;
}) {
  const info = TARGET_LABELS[target];
  const pct = total > 0 ? (count / total) * 100 : 0;

  return (
    <div className="stat-card" style={{ textAlign: 'left', padding: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <span style={{ fontSize: '24px' }}>{info.icon}</span>
        <span style={{ fontSize: '13px', color: 'var(--gray)' }}>{info.label}</span>
      </div>
      <div style={{ fontSize: '28px', fontWeight: 700, color: info.color }}>
        {count}
      </div>
      <div style={{
        height: '4px',
        background: 'var(--light-gray)',
        borderRadius: '2px',
        marginTop: '10px',
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          background: info.color,
          transition: 'width 0.4s ease',
        }} />
      </div>
      <p style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '6px' }}>
        {pct.toFixed(1)}% del total
      </p>
    </div>
  );
}

function StatBlock({
  icon,
  label,
  value,
  color,
}: {
  icon: string;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="stat-card">
      <span className="stat-icon">{icon}</span>
      <span className="stat-value" style={{ color }}>{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}